import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [agree, setAgree] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const { name, email, phone, password } = form;
    if (!name || !email || !phone || !password) {
      toast.error("Please fill all fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!agree) {
      toast.error("Please accept the Terms and Privacy Policy");
      return;
    }
    setLoading(true);
    try {
      await signup(form);
      toast.success("Welcome to Mine AI!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="signup-page">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/32455915/pexels-photo-32455915.png?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1128]/70 via-[#3B5BDB]/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <p className="text-xs uppercase tracking-[0.3em] text-white/80">Mine AI</p>
          <h2 className="font-display text-4xl mt-3 leading-tight">
            Your jewellery expert,<br />always in your pocket.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
          <h1 className="font-display text-3xl sm:text-4xl text-[#0A1128]">
            Create your account
          </h1>
          <p className="mt-2 text-sm text-[#4A5568]">
            Free forever. Upgrade only if you love it.
          </p>
          <div className="mt-8 space-y-4">
            <Field label="Full name">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="signup-name"
                className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="Lakshmi Iyer"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="signup-email"
                className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="signup-phone"
                className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="+91 98xxxxxxxx"
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="signup-password"
                className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="At least 6 characters"
              />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="w-full h-11 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60"
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
            <label className="flex items-start gap-2 text-xs text-[#4A5568] leading-relaxed cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                data-testid="signup-agree"
                className="mt-0.5 accent-[#3B5BDB]"
              />
              <span>
                I agree to Mine AI's{" "}
                <Link to="/terms" target="_blank" className="text-[#3B5BDB] underline">
                  Terms
                </Link>
                ,{" "}
                <Link to="/privacy" target="_blank" className="text-[#3B5BDB] underline">
                  Privacy Policy
                </Link>
                , and{" "}
                <Link to="/ai-policy" target="_blank" className="text-[#3B5BDB] underline">
                  AI Policy
                </Link>
                .
              </span>
            </label>
            <p className="text-sm text-center text-[#4A5568]">
              Already have an account?{" "}
              <Link to="/login" className="text-[#3B5BDB] font-medium" data-testid="link-login">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#4A5568]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
